import { useMemo } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { DocumentType } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { useHouseholdDocumentsQuery } from '../graphql/MultiDocumentUploader.generated';

// Stable default so callers that omit `excludedDocumentTypes` don't hand the
// memos below a fresh array reference on every render.
const NO_EXCLUDED_DOCUMENT_TYPES: DocumentType[] = [];

export function useExistingHouseholdDocumentOptions({
  householdId,
  skip = false,
  excludedDocumentTypes = NO_EXCLUDED_DOCUMENT_TYPES,
}: {
  householdId: string;
  skip?: boolean;
  excludedDocumentTypes?: DocumentType[];
}) {
  const queryProps = useHouseholdDocumentsQuery({
    variables: { householdId: householdId! },
    skip: !householdId || skip,
    fetchPolicy: 'cache-and-network',
  });

  const { data: householdDocuments } = queryProps;

  const documents = useMemo(
    () =>
      getNodes(householdDocuments?.documents).filter(
        (document) => !excludedDocumentTypes.includes(document.type)
      ),
    [householdDocuments?.documents, excludedDocumentTypes]
  );

  // Keep the options array referentially stable across renders so downstream
  // consumers (MUI Autocomplete) don't treat every render as a data change,
  // which would reset in-progress typing. See T2-10778.
  const options: TypeaheadSelectInputOption<string>[] = useMemo(
    () =>
      documents.map((doc) => ({
        value: doc.id,
        display: doc.name,
      })),
    [documents]
  );

  return {
    options,
    ...queryProps,
  };
}
